import { useTranslation } from 'react-i18next';
import { ExternalLink } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import type { CanadaPostTrackingResult } from '../types';
import { getCanadaPostTrackingUrl } from '../types';

interface TrackingTimelineSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tracking: CanadaPostTrackingResult | null;
}

function getEventDotColor(type: string): string {
  const t = type.toUpperCase();
  if (t === 'DELIVERY' || t === 'DELIVERED') return 'bg-green-500';
  if (t.includes('TRANSIT') || t === 'IN_TRANSIT') return 'bg-blue-500';
  if (t === 'OUT_FOR_DELIVERY') return 'bg-yellow-500';
  if (t === 'INDUCTION' || t === 'ACCEPT') return 'bg-gray-400';
  return 'bg-gray-300';
}

function getStatusBadgeKey(tracking: CanadaPostTrackingResult): { key: string; fallback: string; className: string } {
  if (tracking.actualDeliveryDate) {
    return { key: 'delivery.statusDelivered', fallback: 'Delivered', className: 'bg-green-100 text-green-800' };
  }
  if (tracking.events.length > 0) {
    const latest = tracking.events[0].type.toUpperCase();
    if (latest.includes('TRANSIT') || latest === 'IN_TRANSIT') {
      return { key: 'delivery.statusInTransit', fallback: 'In Transit', className: 'bg-blue-100 text-blue-800' };
    }
    if (latest === 'OUT_FOR_DELIVERY') {
      return { key: 'delivery.statusOutForDelivery', fallback: 'Out for Delivery', className: 'bg-yellow-100 text-yellow-800' };
    }
  }
  return { key: 'delivery.statusPending', fallback: 'Pending', className: 'bg-gray-100 text-gray-800' };
}

function formatDateTime(dateStr: string | null, locale: string): string {
  if (!dateStr) return '--';
  try {
    const date = new Date(dateStr);
    return date.toLocaleString(locale === 'fr' ? 'fr-CA' : 'en-CA', {
      timeZone: 'America/Montreal',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return dateStr;
  }
}

function formatDate(dateStr: string | null, locale: string): string {
  if (!dateStr) return '--';
  try {
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString(locale === 'fr' ? 'fr-CA' : 'en-CA', {
      timeZone: 'America/Montreal',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return dateStr;
  }
}

export function TrackingTimelineSheet({ open, onOpenChange, tracking }: TrackingTimelineSheetProps) {
  const { t, i18n } = useTranslation('shippings');

  if (!tracking) return null;

  const statusBadge = getStatusBadgeKey(tracking);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-md overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="text-left">
            {t('delivery.trackingHistory', 'Tracking History')}
          </SheetTitle>
          <div className="space-y-2 pt-2">
            <div className="flex items-center gap-2">
              <span className="font-mono text-sm">{tracking.pin}</span>
              <Badge variant="outline" className={statusBadge.className}>
                {t(statusBadge.key, statusBadge.fallback)}
              </Badge>
            </div>
            {tracking.serviceName && (
              <div className="text-sm text-muted-foreground">{tracking.serviceName}</div>
            )}
            {tracking.actualDeliveryDate ? (
              <div className="text-sm font-medium text-green-700">
                {t('delivery.deliveredOn', { date: formatDate(tracking.actualDeliveryDate, i18n.language) })}
              </div>
            ) : tracking.expectedDeliveryDate ? (
              <div className="text-sm text-muted-foreground">
                {t('delivery.expectedDelivery', { date: formatDate(tracking.expectedDeliveryDate, i18n.language) })}
              </div>
            ) : null}
          </div>
        </SheetHeader>

        <div className="mt-6">
          {tracking.events.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              {t('delivery.noEvents', 'No tracking events available')}
            </div>
          ) : (
            <div className="relative pl-6">
              <div className="absolute left-[9px] top-2 bottom-2 w-px bg-gray-200" />
              {tracking.events.map((event, index) => (
                <div key={index} className="relative pb-6 last:pb-0">
                  <div
                    className={`absolute left-[-15px] top-1.5 h-[10px] w-[10px] rounded-full border-2 border-white ${getEventDotColor(event.type)}`}
                  />
                  <div className="ml-2">
                    <div className="text-sm font-medium">{event.description}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {formatDateTime(event.date, i18n.language)}
                    </div>
                    {event.location && (
                      <div className="text-xs text-muted-foreground">
                        {event.location}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="mt-6 pt-4 border-t">
          <Button variant="outline" size="sm" className="w-full" asChild>
            <a
              href={getCanadaPostTrackingUrl(tracking.pin)}
              target="_blank"
              rel="noopener noreferrer"
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              {t('delivery.trackOnCanadaPost', 'Track on Canada Post')}
            </a>
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
